import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useAdmin } from '@lok/context/AdminContext';
import SaveStatusIndicator from './SaveStatusIndicator';

const AdminControls = () => {
  const {
    isAdminView,
    toggleAdminView,
    runActiveUndo,
    hasActiveUndo,
    hasUndo,
    popUndo
  } = useAdmin();
  const [isExpanded, setIsExpanded] = useState(true);

  // Undo is available when an editor is open (Ctrl+Z step) or there's a saved
  // change to revert. Drives both the enabled state and the active styling so
  // the button reads as actionable — not greyed out — whenever it can do
  // something (CON-183).
  const canUndo = hasUndo || hasActiveUndo;

  if (!isAdminView) return null;

  const handleUndo = async () => {
    // While an editor is open, Undo behaves like Ctrl+Z: step back one change
    // inside the live editor. Only once that editor has nothing left to undo
    // (runActiveUndo returns false) do we fall back to reverting the last
    // SAVED change off the stack. Previously the button ignored the open
    // editor entirely and quietly reverted an older save (CON-183).
    if (runActiveUndo()) return;
    const entry = popUndo();
    if (!entry?.revert) {
      toast.info('Nothing to undo');
      return;
    }
    try {
      await entry.revert();
      toast.success('Change reverted');
    } catch {
      toast.error('Failed to undo change');
    }
  };

  return (
    <div
      data-admin-controls=''
      className='fixed top-20 right-52 z-[1001] min-w-62 bg-white dark:bg-[#1D2838] shadow-lg rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex flex-col gap-2'
    >
      <button
        type='button'
        onClick={() => setIsExpanded(!isExpanded)}
        className='flex items-center justify-between w-full hover:opacity-80 transition-opacity'
      >
        <p className='text-[10px] font-semibold text-[#637381] dark:text-gray-400 uppercase tracking-wider'>
          Admin Controls
        </p>
        {isExpanded ? (
          <ChevronUp className='w-4 h-4 text-[#637381] dark:text-gray-400' />
        ) : (
          <ChevronDown className='w-4 h-4 text-[#637381] dark:text-gray-400' />
        )}
      </button>

      {/* Autosave status — visible whether the panel is expanded or not */}
      <SaveStatusIndicator />

      {isExpanded && (
        <>
          {/* CON-190 follow-up: no "Save Changes" button — lesson content
              autosaves as you type and finalizes on click-away; module/lesson
              reordering saves on drop. Undo (Ctrl+Z) reverts. */}
          <button
            type='button'
            onClick={handleUndo}
            disabled={!canUndo}
            className={`w-full text-left px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
              canUndo
                ? 'bg-primary-green hover:bg-primary-green/80 text-primary-green-fg cursor-pointer'
                : 'bg-gray-100 dark:bg-[#2A3441] text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60'
            }`}
          >
            Undo
          </button>
          <button
            type='button'
            onClick={toggleAdminView}
            className='w-full text-left px-3 py-1.5 rounded-lg bg-primary-green hover:bg-primary-green/80 text-primary-green-fg text-sm font-semibold transition-colors'
          >
            Preview as Learner
          </button>
        </>
      )}
    </div>
  );
};

export default AdminControls;
